class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.bind();
    if (!this.boundRefresh) {
      this.boundRefresh = () => this.refresh(true);
      document.addEventListener('cart:refresh', this.boundRefresh);
    }
  }
  bind() {
    this.querySelectorAll('[data-close]').forEach((button) => button.addEventListener('click', () => this.close()));
  }
  open() {
    this.setAttribute('open', '');
    document.body.classList.add('drawer-open');
    this.querySelector('.drawer__panel')?.focus();
  }
  close() {
    this.removeAttribute('open');
    document.body.classList.remove('drawer-open');
  }
  showError(message) {
    const box = this.querySelector('[data-cart-error]');
    if (box) box.textContent = message || '';
  }
  async refresh(open = false) {
    try {
      const response = await fetch(`${theme.routes.root}?sections=cart-drawer`);
      if (!response.ok) throw new Error('Unable to refresh your bag.');
      const sections = await response.json();
      const doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      const replacement = doc.querySelector('cart-drawer');
      if (!replacement) throw new Error('Unable to refresh your bag.');
      this.innerHTML = replacement.innerHTML;
      this.bind();
      const count = this.querySelector('[data-drawer-count]')?.textContent?.replace(/\D/g, '') || '0';
      document.querySelectorAll('[data-cart-count]').forEach((element) => { element.textContent = count; });
      if (open) this.open();
    } catch (error) {
      this.showError(error.message);
    }
  }
}
customElements.define('cart-drawer', CartDrawer);

document.addEventListener('click', async (event) => {
  const opener = event.target.closest('[data-cart-open]');
  if (opener) {
    event.preventDefault();
    document.querySelector('cart-drawer')?.open();
    return;
  }
  const change = event.target.closest('[data-cart-change]');
  if (!change) return;
  event.preventDefault();
  change.disabled = true;
  try {
    const response = await fetch(theme.routes.cartChange, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: change.dataset.key, quantity: Number(change.dataset.quantity) })
    });
    if (!response.ok) throw await response.json();
    await document.querySelector('cart-drawer')?.refresh(true);
  } catch (error) {
    document.querySelector('cart-drawer')?.showError(error.description || 'Your bag could not be updated. Please try again.');
  } finally {
    change.disabled = false;
  }
});

document.addEventListener('submit', async (event) => {
  const form = event.target.closest('form[action*="/cart/add"]');
  if (!form || form.hasAttribute('data-native-submit')) return;
  if (event.submitter && event.submitter.name !== 'add') return;
  event.preventDefault();
  const button = event.submitter || form.querySelector('[name="add"]');
  button?.setAttribute('aria-busy', 'true');
  const errorBox = form.querySelector('[data-product-error]');
  if (errorBox) errorBox.textContent = '';
  try {
    const response = await fetch(theme.routes.cartAdd, { method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' } });
    if (!response.ok) throw await response.json();
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  } catch (error) {
    if (errorBox) errorBox.textContent = error.description || 'This item could not be added. Please try again.';
  } finally {
    button?.removeAttribute('aria-busy');
  }
});

class HeroSlider extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('.hero-slide')];
    this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
    this.delay = Number(this.dataset.delay || 6000);
    this.autoplay = this.dataset.autoplay === 'true' && !matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.paused = !this.autoplay;
    this.querySelectorAll('[data-slide-to]').forEach((button) => button.addEventListener('click', () => { this.show(Number(button.dataset.slideTo)); this.restart(); }));
    this.querySelector('[data-prev]')?.addEventListener('click', () => { this.show(this.index - 1); this.restart(); });
    this.querySelector('[data-next]')?.addEventListener('click', () => { this.show(this.index + 1); this.restart(); });
    this.querySelector('[data-pause]')?.addEventListener('click', () => this.togglePause());
    this.addEventListener('mouseenter', () => this.stop());
    this.addEventListener('mouseleave', () => { if (!this.paused) this.start(); });
    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', () => { if (!this.paused) this.start(); });
    this.addEventListener('touchstart', (event) => { this.touchX = event.changedTouches[0].clientX; }, { passive: true });
    this.addEventListener('touchend', (event) => {
      const distance = event.changedTouches[0].clientX - this.touchX;
      if (Math.abs(distance) > 45) this.show(this.index + (distance < 0 ? 1 : -1));
      this.restart();
    }, { passive: true });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.show(this.index - 1);
      if (event.key === 'ArrowRight') this.show(this.index + 1);
    });
    if (this.autoplay) this.start();
  }
  show(number) {
    if (!this.slides.length) return;
    this.index = (number + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, index) => {
      const active = index === this.index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', String(!active));
    });
    this.querySelectorAll('[data-slide-to]').forEach((button, index) => {
      button.classList.toggle('is-active', index === this.index);
      if (index === this.index) button.setAttribute('aria-current', 'true'); else button.removeAttribute('aria-current');
    });
  }
  start() { this.stop(); if (!this.paused) this.timer = setInterval(() => this.show(this.index + 1), this.delay); }
  stop() { clearInterval(this.timer); }
  restart() { if (!this.paused) this.start(); }
  togglePause() {
    this.paused = !this.paused;
    const button = this.querySelector('[data-pause]');
    button?.setAttribute('aria-pressed', String(this.paused));
    button?.setAttribute('aria-label', this.paused ? 'Play carousel' : 'Pause carousel');
    const label = this.querySelector('[data-pause-label]');
    if (label) label.textContent = this.paused ? 'Play' : 'Pause';
    if (this.paused) this.stop(); else this.start();
  }
}
customElements.define('hero-slider', HeroSlider);

class ProductInfo extends HTMLElement {
  connectedCallback() {
    this.data = JSON.parse(this.querySelector('[data-product-json]').textContent);
    this.form = this.querySelector('form[action*="/cart/add"]');
    this.radios = [...this.querySelectorAll('[data-option-input]')];
    this.radios.forEach((radio) => radio.addEventListener('change', () => this.update()));
    this.querySelectorAll('[data-media-thumb]').forEach((thumb) => thumb.addEventListener('click', () => this.selectMedia(thumb.dataset.mediaThumb)));
    this.querySelectorAll('[data-quantity-change]').forEach((button) => button.addEventListener('click', () => this.changeQuantity(Number(button.dataset.quantityChange))));
    this.sticky = this.querySelector('[data-sticky-atc]');
    this.purchaseArea = this.querySelector('[data-main-purchase-area]');
    this.querySelector('[data-sticky-add]')?.addEventListener('click', () => this.form.requestSubmit(this.form.querySelector('[name="add"]')));
    if (this.sticky && this.purchaseArea) {
      this.syncStickyVisibility = () => {
        const show = this.purchaseArea.getBoundingClientRect().bottom < 0;
        this.sticky.classList.toggle('is-visible', show);
        this.sticky.setAttribute('aria-hidden', String(!show));
      };
      addEventListener('scroll', this.syncStickyVisibility, { passive: true });
      addEventListener('resize', this.syncStickyVisibility, { passive: true });
      this.syncStickyVisibility();
    }
    this.update();
  }
  selectMedia(id) {
    const target = this.querySelector(`[data-media-id="${id}"]`);
    if (!target) return;
    this.querySelectorAll('[data-media-id]').forEach((media) => media.classList.toggle('is-selected', media === target));
    this.querySelectorAll('[data-media-thumb]').forEach((thumb) => thumb.classList.toggle('is-selected', thumb.dataset.mediaThumb === String(id)));
    if (matchMedia('(max-width: 640px)').matches) target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }
  changeQuantity(delta) {
    const input = this.form.querySelector('[data-quantity-input]');
    input.value = Math.max(1, Number(input.value) + delta);
    this.querySelector('[data-quantity-display]').textContent = input.value;
  }
  update() {
    const values = [...this.querySelectorAll('[data-option-group]')].map((group) => group.querySelector('input:checked')?.value);
    const variant = this.data.variants.find((item) => item.options.every((option, index) => option === values[index]));
    if (!variant) return;
    this.form.querySelector('[name="id"]').value = variant.id;
    this.querySelectorAll('[data-option-group]').forEach((group) => {
      const selected = group.querySelector('input:checked')?.value || '';
      const label = group.querySelector('[data-option-selected]');
      if (label) label.textContent = selected;
    });
    this.querySelector('[data-price]').textContent = this.format(variant.price);
    const buttonPrice = this.querySelector('[data-button-price]');
    if (buttonPrice) buttonPrice.textContent = this.format(variant.price);
    const compare = this.querySelector('[data-compare-price]');
    if (compare) { compare.textContent = variant.compareAtPrice ? this.format(variant.compareAtPrice) : ''; compare.hidden = !(variant.compareAtPrice > variant.price); }
    this.querySelector('[data-sku]').textContent = variant.sku || '—';
    this.querySelector('[data-inventory-count]').textContent = variant.inventoryManagement ? Math.max(0, variant.inventoryQuantity) : 'Not tracked';
    const status = this.querySelector('[data-inventory]');
    status.textContent = variant.available ? 'In stock' : 'Currently unavailable';
    status.classList.toggle('is-out', !variant.available);
    const add = this.form.querySelector('[name="add"]');
    add.disabled = !variant.available;
    add.querySelector('span').textContent = variant.available ? 'Add to Cart' : 'Sold Out';
    const stickyAdd = this.querySelector('[data-sticky-add]');
    if (stickyAdd) { stickyAdd.disabled = !variant.available; stickyAdd.textContent = variant.available ? 'Add to Cart' : 'Sold Out'; }
    const stickyVariant = this.querySelector('[data-sticky-variant]');
    if (stickyVariant) stickyVariant.textContent = variant.title;
    const stickyPrice = this.querySelector('[data-sticky-price]');
    if (stickyPrice) stickyPrice.textContent = this.format(variant.price);
    if (variant.featuredMediaId) this.selectMedia(variant.featuredMediaId);
    this.updateSpecs(values, variant);
    history.replaceState({}, '', `${location.pathname}?variant=${variant.id}`);
  }
  updateSpecs(values, variant) {
    const names = this.data.optionNames || [];
    const packIndex = names.findIndex((name) => /(pack|package)/i.test(name));
    const sizeIndex = names.findIndex((name) => /size/i.test(name));
    const countIndex = names.findIndex((name) => /(count|quantity|pads)/i.test(name) && !/(pack|package) size/i.test(name));
    const dimensionOption = values.find((value) => /(\d+\s*[×x]\s*\d+|\bcm\b|\bin\b|["″])/i.test(value || ''));
    const primaryIndex = packIndex >= 0 ? packIndex : sizeIndex >= 0 ? sizeIndex : 0;
    const primary = values[primaryIndex] || variant.title;
    const count = variant.count || (countIndex >= 0 && countIndex !== primaryIndex ? values[countIndex] : '');
    const dimensions = variant.dimensions || dimensionOption || '';
    const primaryLabel = this.querySelector('[data-spec-primary-label]');
    if (primaryLabel) primaryLabel.textContent = packIndex >= 0 ? 'Package Size' : sizeIndex >= 0 ? 'Selected size' : 'Selected option';
    this.querySelector('[data-spec-primary]').textContent = primary;
    this.querySelector('[data-spec-dimensions]').textContent = dimensions;
    this.querySelector('[data-spec-count]').textContent = count ? `${count}${/^\d+$/.test(String(count)) ? ' pads' : ''}` : '';
    const dimensionRow = this.querySelector('[data-spec-dimensions-row]');
    const countRow = this.querySelector('[data-spec-count-row]');
    if (dimensionRow) dimensionRow.hidden = !dimensions;
    if (countRow) countRow.hidden = !count;
    this.querySelector('[data-spec-variant]').textContent = variant.title;
  }
  format(cents) { return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: this.data.currency }).format(cents / 100); }
}
customElements.define('product-info', ProductInfo);

class ImageLightbox extends HTMLElement {
  connectedCallback() {
    this.image = this.querySelector('[data-lightbox-image]');
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-lightbox-src]');
      if (trigger) { event.preventDefault(); this.open(trigger); }
    });
    this.querySelectorAll('[data-lightbox-close]').forEach((button) => button.addEventListener('click', () => this.close()));
    this.querySelector('[data-lightbox-prev]')?.addEventListener('click', () => this.move(-1));
    this.querySelector('[data-lightbox-next]')?.addEventListener('click', () => this.move(1));
    this.image?.addEventListener('click', () => this.image.classList.toggle('is-zoomed'));
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab' || !this.hasAttribute('open')) return;
      const focusable = [...this.querySelectorAll('button:not([disabled])')];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });
  }
  open(trigger) {
    this.trigger = trigger;
    this.items = [...document.querySelectorAll('[data-lightbox-src]')];
    this.index = this.items.indexOf(trigger);
    this.show();
    this.setAttribute('open', '');
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('lightbox-open');
    this.querySelector('[data-lightbox-close]')?.focus();
  }
  show() {
    const item = this.items[this.index];
    if (!item) return;
    this.image.src = item.dataset.lightboxSrc;
    this.image.alt = item.dataset.lightboxAlt || '';
    this.image.classList.remove('is-zoomed');
  }
  move(delta) { this.index = (this.index + delta + this.items.length) % this.items.length; this.show(); }
  close() { if (!this.hasAttribute('open')) return; this.removeAttribute('open'); this.setAttribute('aria-hidden', 'true'); document.body.classList.remove('lightbox-open'); this.image.src = ''; this.trigger?.focus(); }
}
customElements.define('image-lightbox', ImageLightbox);

function setMobileNavigation(open) {
  document.body.classList.toggle('nav-open', open);
  document.querySelector('.mobile-nav')?.setAttribute('aria-hidden', String(!open));
  document.querySelectorAll('[data-mobile-nav-toggle]').forEach((button) => button.setAttribute('aria-expanded', String(open)));
}
document.querySelectorAll('[data-mobile-nav-toggle]').forEach((button) => button.addEventListener('click', () => setMobileNavigation(!document.body.classList.contains('nav-open'))));
document.querySelectorAll('.mobile-nav a').forEach((link) => link.addEventListener('click', () => setMobileNavigation(false)));

function syncFooterAccordions() {
  const mobile = matchMedia('(max-width: 640px)').matches;
  document.querySelectorAll('[data-footer] .footer__column').forEach((details) => {
    if (!mobile) details.open = true;
    else if (!details.dataset.mobileReady) { details.open = false; details.dataset.mobileReady = 'true'; }
  });
}
syncFooterAccordions();
window.addEventListener('resize', syncFooterAccordions);
document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') { setMobileNavigation(false); document.querySelector('cart-drawer')?.close(); document.querySelector('image-lightbox')?.close(); }
  const lightbox = document.querySelector('image-lightbox[open]');
  if (event.key === 'ArrowLeft') lightbox?.move(-1);
  if (event.key === 'ArrowRight') lightbox?.move(1);
});
